import { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus,
  Layers,
  FileText,
  MoreHorizontal,
  Archive,
  Trash2,
  Trash,
  Pin,
  PinOff,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import clsx from "clsx";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Accent, Thread } from "../lib/types";
import { ACCENTS, accentClasses } from "../lib/types";

export const BIN_ID = "__bin__";

type Props = {
  threads: Thread[];
  activeId: string | null;
  binCount: number;
  onSelect: (id: string) => void;
  onCreate: (name: string, kind: "lanes" | "flat", accent: Accent) => void;
  onRename: (id: string, name: string) => void;
  onChangeAccent: (id: string, accent: Accent) => void;
  onTogglePin: (id: string, pinned: boolean) => void;
  onReorder: (orderedIds: string[]) => void;
  onArchive: (id: string) => void;
  onDelete: (id: string) => void;
};

export function Sidebar({
  threads,
  activeId,
  binCount,
  onSelect,
  onCreate,
  onRename,
  onChangeAccent,
  onTogglePin,
  onReorder,
  onArchive,
  onDelete,
}: Props) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newKind, setNewKind] = useState<"lanes" | "flat">("lanes");
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close the open thread menu on outside click or Escape.
  useEffect(() => {
    if (!menuFor) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target)) return;
      const el = (e.target as HTMLElement | null)?.closest?.(
        "[data-thread-menu-trigger]"
      );
      if (el) return;
      setMenuFor(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuFor(null);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuFor]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Collapsed sidebar preference: rail mode when true. Persisted so the
  // choice survives reloads (this is a UI preference, not a secret —
  // unlike the auth state, which is deliberately in-memory only).
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("workit:sidebar-collapsed") === "1";
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(
        "workit:sidebar-collapsed",
        collapsed ? "1" : "0"
      );
    } catch {
      // localStorage can throw in private-browsing / disabled-storage
      // modes. The sidebar still works — it just won't remember the
      // preference across reloads.
    }
  }, [collapsed]);

  // ⌘\ toggles the rail — VSCode uses ⌘B, but B is already bold in the
  // editor, so we go with \ (also unambiguous with any card shortcut).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "\\") {
        e.preventDefault();
        setCollapsed((c) => !c);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const pinned = useMemo(() => threads.filter((t) => t.pinned), [threads]);
  const unpinned = useMemo(() => threads.filter((t) => !t.pinned), [threads]);

  const handleDragEnd = (e: DragEndEvent, group: "pinned" | "unpinned") => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const source = group === "pinned" ? pinned : unpinned;
    const oldIdx = source.findIndex((t) => t.id === active.id);
    const newIdx = source.findIndex((t) => t.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = arrayMove(source, oldIdx, newIdx);
    // Bubble up just the IDs in their new order — App will assign sort_orders
    // for the whole thread list and persist via /api/reorder.
    const otherGroup = group === "pinned" ? unpinned : pinned;
    const merged =
      group === "pinned"
        ? [...reordered, ...otherGroup]
        : [...otherGroup, ...reordered];
    onReorder(merged.map((t) => t.id));
  };

  // Rail (collapsed) mode: a slim 56 px strip that keeps the essentials
  // one click away — brand mark, new-thread, colored thread dots grouped
  // pinned-vs-rest, bin, and the chevron to expand again. Uses native
  // `title` tooltips for thread names since there's no room to render
  // them inline.
  if (collapsed) {
    return (
      <aside className="flex h-full w-14 shrink-0 flex-col border-r border-border bg-surface transition-[width] duration-200 ease-out">
        <div className="flex items-center justify-center pt-6 pb-4">
          <h1 className="font-semibold text-lg tracking-tight" title="workIt.">
            w<span className="text-lavender-400">.</span>
          </h1>
        </div>

        <div className="flex justify-center pb-3">
          <button
            onClick={() => {
              setCollapsed(false);
              // Defer so the rail transition doesn't fight the input
              // autofocus for layout; the "new thread" input appears in
              // the expanded sidebar on next tick.
              setTimeout(() => setCreating(true), 220);
            }}
            title="new thread (N)"
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-dashed border-border-strong/70 text-ink-soft transition-colors hover:border-lavender-200 hover:bg-lavender-50/40 hover:text-ink"
          >
            <Plus size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-1">
          {pinned.length > 0 && (
            <>
              <ul className="flex flex-col items-center gap-1.5 px-1">
                {pinned.map((t) => (
                  <RailThreadDot
                    key={t.id}
                    thread={t}
                    isActive={t.id === activeId}
                    onSelect={onSelect}
                  />
                ))}
              </ul>
              <div className="mx-3 my-2 border-t border-border/60" />
            </>
          )}
          <ul className="flex flex-col items-center gap-1.5 px-1">
            {unpinned.map((t) => (
              <RailThreadDot
                key={t.id}
                thread={t}
                isActive={t.id === activeId}
                onSelect={onSelect}
              />
            ))}
          </ul>
        </div>

        <button
          onClick={() => onSelect(BIN_ID)}
          title={binCount > 0 ? `bin (${binCount})` : "bin"}
          className={clsx(
            "mx-2 mb-1 flex h-9 items-center justify-center rounded-lg transition-colors",
            activeId === BIN_ID
              ? "bg-blush-50 text-ink"
              : "text-ink-soft hover:bg-canvas hover:text-ink"
          )}
        >
          <Trash size={14} className="text-ink-mute" />
        </button>

        <div className="flex justify-center border-t border-border py-2">
          <button
            onClick={() => setCollapsed(false)}
            title="expand sidebar (⌘\\)"
            className="flex h-7 w-7 items-center justify-center rounded-md text-ink-mute transition-colors hover:bg-canvas hover:text-ink-soft"
          >
            <ChevronsRight size={14} />
          </button>
        </div>
      </aside>
    );
  }

  return (
    <aside className="flex h-full w-72 shrink-0 flex-col border-r border-border bg-surface transition-[width] duration-200 ease-out">
      <div className="flex items-center justify-between px-5 pt-6 pb-5">
        <div>
          <h1 className="font-semibold text-xl tracking-tight">
            workIt<span className="text-lavender-400">.</span>
          </h1>
          <p className="mt-0.5 text-xs text-ink-mute">your space to think</p>
        </div>
      </div>

      <div className="px-3">
        {creating ? (
          <div className="rounded-lane border border-border bg-surface p-3 shadow-soft">
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newName.trim()) {
                  onCreate(newName.trim(), newKind, "lavender");
                  setNewName("");
                  setCreating(false);
                } else if (e.key === "Escape") {
                  setCreating(false);
                  setNewName("");
                }
              }}
              placeholder="thread name"
              className="input-bare w-full text-sm"
            />
            <div className="mt-3 flex items-center justify-between">
              <div className="flex gap-1 text-xs">
                <button
                  className={clsx(
                    "rounded-md px-2 py-1 transition-colors",
                    newKind === "lanes"
                      ? "bg-lavender-50 text-lavender-400"
                      : "text-ink-soft hover:bg-border/50"
                  )}
                  onClick={() => setNewKind("lanes")}
                >
                  <Layers className="inline -mt-0.5 mr-1" size={12} /> lanes
                </button>
                <button
                  className={clsx(
                    "rounded-md px-2 py-1 transition-colors",
                    newKind === "flat"
                      ? "bg-lavender-50 text-lavender-400"
                      : "text-ink-soft hover:bg-border/50"
                  )}
                  onClick={() => setNewKind("flat")}
                >
                  <FileText className="inline -mt-0.5 mr-1" size={12} /> flat
                </button>
              </div>
              <button
                className="text-xs text-ink-mute hover:text-ink-soft"
                onClick={() => {
                  setCreating(false);
                  setNewName("");
                }}
              >
                cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setCreating(true)}
            className="flex w-full items-center justify-between rounded-lane border border-dashed border-border-strong/70 px-3 py-2.5 text-sm text-ink-soft transition-colors hover:border-lavender-200 hover:bg-lavender-50/40 hover:text-ink"
          >
            <span className="flex items-center gap-2">
              <Plus size={14} />
              new thread
            </span>
            <span className="font-mono text-[10px] text-ink-mute">N</span>
          </button>
        )}
      </div>

      <div className="mt-4 flex-1 overflow-y-auto px-2 pb-4">
        {pinned.length > 0 && (
          <>
            <div className="px-3 pb-2 text-[10px] font-medium uppercase tracking-wider text-ink-mute">
              pinned
            </div>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(e) => handleDragEnd(e, "pinned")}
            >
              <SortableContext
                items={pinned.map((t) => t.id)}
                strategy={verticalListSortingStrategy}
              >
                <ul className="space-y-0.5">
                  {pinned.map((t) => (
                    <SidebarThread
                      key={t.id}
                      thread={t}
                      isActive={t.id === activeId}
                      isRenaming={renaming === t.id}
                      menuOpen={menuFor === t.id}
                      onSelect={onSelect}
                      onStartRename={() => setRenaming(t.id)}
                      onCommitRename={(name) => {
                        if (name && name !== t.name) onRename(t.id, name);
                        setRenaming(null);
                      }}
                      onMenuToggle={() =>
                        setMenuFor(menuFor === t.id ? null : t.id)
                      }
                      onCloseMenu={() => setMenuFor(null)}
                      onChangeAccent={onChangeAccent}
                      onTogglePin={onTogglePin}
                      onArchive={onArchive}
                      onDelete={onDelete}
                      menuRef={menuFor === t.id ? menuRef : undefined}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
            <div className="mb-2 mt-3 mx-3 border-t border-border/60" />
          </>
        )}

        <div className="px-3 pb-2 text-[10px] font-medium uppercase tracking-wider text-ink-mute">
          threads
        </div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(e) => handleDragEnd(e, "unpinned")}
        >
          <SortableContext
            items={unpinned.map((t) => t.id)}
            strategy={verticalListSortingStrategy}
          >
            <ul className="space-y-0.5">
              {unpinned.map((t) => (
                <SidebarThread
                  key={t.id}
                  thread={t}
                  isActive={t.id === activeId}
                  isRenaming={renaming === t.id}
                  menuOpen={menuFor === t.id}
                  onSelect={onSelect}
                  onStartRename={() => setRenaming(t.id)}
                  onCommitRename={(name) => {
                    if (name && name !== t.name) onRename(t.id, name);
                    setRenaming(null);
                  }}
                  onMenuToggle={() =>
                    setMenuFor(menuFor === t.id ? null : t.id)
                  }
                  onCloseMenu={() => setMenuFor(null)}
                  onChangeAccent={onChangeAccent}
                  onTogglePin={onTogglePin}
                  onArchive={onArchive}
                  onDelete={onDelete}
                  menuRef={menuFor === t.id ? menuRef : undefined}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>

        {threads.length === 0 && !creating && (
          <div className="mt-12 px-4 text-center text-xs text-ink-mute">
            no threads yet. press{" "}
            <kbd className="rounded bg-canvas px-1 py-0.5 font-mono text-[10px]">
              N
            </kbd>{" "}
            to start.
          </div>
        )}
      </div>

      <button
        onClick={() => onSelect(BIN_ID)}
        className={clsx(
          "mx-2 mb-1 flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors",
          activeId === BIN_ID
            ? "bg-blush-50 text-ink"
            : "text-ink-soft hover:bg-canvas hover:text-ink"
        )}
      >
        <span className="flex items-center gap-2.5">
          <Trash size={13} className="text-ink-mute" />
          bin
        </span>
        {binCount > 0 && (
          <span className="font-mono text-[10px] text-ink-mute">{binCount}</span>
        )}
      </button>

      <div className="flex items-center justify-between border-t border-border px-5 py-3 text-[11px] text-ink-mute">
        <span>breathe. one card at a time.</span>
        <button
          onClick={() => setCollapsed(true)}
          title="collapse sidebar (⌘\\)"
          className="flex h-6 w-6 items-center justify-center rounded-md text-ink-mute transition-colors hover:bg-canvas hover:text-ink-soft"
        >
          <ChevronsLeft size={13} />
        </button>
      </div>
    </aside>
  );
}

// ----------------------------------------------------------------------------
// RailThreadDot: a single collapsed-sidebar thread affordance. Just a
// colored dot with the thread's accent, tooltip = name, click = switch.
// Active thread gets a subtle background ring so it's obvious which one
// you're currently on without needing the full row layout.
// ----------------------------------------------------------------------------

type RailThreadDotProps = {
  thread: Thread;
  isActive: boolean;
  onSelect: (id: string) => void;
};

function RailThreadDot({ thread, isActive, onSelect }: RailThreadDotProps) {
  const accent = accentClasses[thread.accent];
  return (
    <li className="w-full">
      <button
        onClick={() => onSelect(thread.id)}
        title={thread.pinned ? `${thread.name} · pinned` : thread.name}
        className={clsx(
          "group flex h-8 w-full items-center justify-center rounded-lg transition-colors",
          isActive ? accent.sidebarActive : "hover:bg-canvas"
        )}
      >
        <span
          className={clsx(
            "h-2.5 w-2.5 rounded-full transition-transform group-hover:scale-110",
            accent.dot
          )}
        />
      </button>
    </li>
  );
}

// ----------------------------------------------------------------------------
// SidebarThread: one row in the threads list.
// ----------------------------------------------------------------------------

type ThreadRowProps = {
  thread: Thread;
  isActive: boolean;
  isRenaming: boolean;
  menuOpen: boolean;
  onSelect: (id: string) => void;
  onStartRename: () => void;
  onCommitRename: (name: string) => void;
  onMenuToggle: () => void;
  onCloseMenu: () => void;
  onChangeAccent: (id: string, accent: Accent) => void;
  onTogglePin: (id: string, pinned: boolean) => void;
  onArchive: (id: string) => void;
  onDelete: (id: string) => void;
  menuRef?: React.RefObject<HTMLDivElement | null>;
};

function SidebarThread({
  thread: t,
  isActive,
  isRenaming,
  menuOpen,
  onSelect,
  onStartRename,
  onCommitRename,
  onMenuToggle,
  onCloseMenu,
  onChangeAccent,
  onTogglePin,
  onArchive,
  onDelete,
  menuRef,
}: ThreadRowProps) {
  const accent = accentClasses[t.accent];
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: t.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <li ref={setNodeRef} style={style} className="relative">
      {isRenaming ? (
        <input
          autoFocus
          defaultValue={t.name}
          onBlur={(e) => onCommitRename(e.target.value.trim())}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            if (e.key === "Escape") onCommitRename(t.name);
          }}
          className="input-bare w-full rounded-lg px-3 py-2 text-sm bg-canvas"
        />
      ) : (
        <div
          {...attributes}
          {...listeners}
          onClick={() => onSelect(t.id)}
          onDoubleClick={onStartRename}
          className={clsx(
            "group flex w-full cursor-grab items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors active:cursor-grabbing",
            isActive
              ? accent.sidebarActive
              : "text-ink-soft hover:bg-canvas hover:text-ink"
          )}
        >
          <span className={clsx("h-2 w-2 shrink-0 rounded-full", accent.dot)} />
          <span className="truncate flex-1">{t.name}</span>
          {t.pinned ? (
            <Pin size={11} className="shrink-0 -rotate-45 text-ink-mute" />
          ) : null}
          <span
            role="button"
            tabIndex={0}
            data-thread-menu-trigger
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onMenuToggle();
            }}
            className={clsx(
              "shrink-0 rounded p-0.5 transition-opacity",
              menuOpen
                ? "opacity-100"
                : "opacity-0 group-hover:opacity-100",
              "text-ink-mute hover:text-ink hover:bg-border/40"
            )}
          >
            <MoreHorizontal size={14} />
          </span>
        </div>
      )}

      {menuOpen && (
        <div
          ref={menuRef}
          className="absolute right-2 top-9 z-20 w-44 rounded-lg border border-border bg-surface p-1 shadow-modal"
        >
          <button
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-ink-soft hover:bg-canvas hover:text-ink"
            onClick={() => {
              onStartRename();
              onCloseMenu();
            }}
          >
            rename
          </button>
          <button
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-ink-soft hover:bg-canvas hover:text-ink"
            onClick={() => {
              onTogglePin(t.id, !t.pinned);
              onCloseMenu();
            }}
          >
            {t.pinned ? (
              <>
                <PinOff size={12} /> unpin
              </>
            ) : (
              <>
                <Pin size={12} /> pin to top
              </>
            )}
          </button>
          <div className="my-1 h-px bg-border/60" />
          <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-ink-mute">
            color
          </div>
          <div className="grid grid-cols-7 gap-1 px-2 py-1">
            {ACCENTS.map((a) => (
              <button
                key={a}
                title={a}
                onClick={() => {
                  onChangeAccent(t.id, a);
                  onCloseMenu();
                }}
                className={clsx(
                  "h-4 w-4 rounded-full border border-black/5 transition-transform hover:scale-110",
                  accentClasses[a].dot,
                  t.accent === a && "ring-2 ring-offset-1 ring-ink-faint"
                )}
              />
            ))}
          </div>
          <div className="my-1 h-px bg-border/60" />
          <button
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-ink-soft hover:bg-canvas hover:text-ink"
            onClick={() => {
              onArchive(t.id);
              onCloseMenu();
            }}
          >
            <Archive size={12} /> archive
          </button>
          <button
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-blush-400 hover:bg-blush-50"
            onClick={() => {
              // App's handleDelete owns the themed confirm dialog now —
              // don't double-prompt with the native browser confirm.
              onDelete(t.id);
              onCloseMenu();
            }}
          >
            <Trash2 size={12} /> delete
          </button>
        </div>
      )}
    </li>
  );
}
